import { useEffect, useState, useCallback } from 'react';
import { getTgWebApp, isInTelegram } from '@/lib/telegram';

type Theme = 'light' | 'dark';
const KEY = 'gn:theme';

function telegramTheme(): Theme | null {
  if (isInTelegram()) {
    const tg = getTgWebApp();
    if (tg?.colorScheme === 'light' || tg?.colorScheme === 'dark') return tg.colorScheme;
  }
  return null;
}

function savedTheme(): Theme | null {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // Storage can be unavailable in embedded and privacy-restricted browsers.
  }
  return null;
}

function systemTheme(): Theme {
  return typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function useTheme() {
  const [followsSystem, setFollowsSystem] = useState(() => !telegramTheme() && !savedTheme());
  const [theme, setThemeState] = useState<Theme>(() => {
    const fromTelegram = telegramTheme();
    if (fromTelegram) {
      document.documentElement.setAttribute('data-theme', fromTelegram);
      return fromTelegram;
    }
    const saved = savedTheme();
    if (saved) return saved;
    const fromDocument = document.documentElement.getAttribute('data-theme');
    return fromDocument === 'light' || fromDocument === 'dark' ? fromDocument : systemTheme();
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    if (!isInTelegram() && !followsSystem) {
      try {
        localStorage.setItem(KEY, theme);
      } catch {
        // The applied theme still works for the current page.
      }
    }
  }, [followsSystem, theme]);

  useEffect(() => {
    if (!followsSystem || isInTelegram() || typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const applySystemTheme = (matches: boolean) => setThemeState(matches ? 'dark' : 'light');
    applySystemTheme(media.matches);
    const onChange = (event: MediaQueryListEvent) => applySystemTheme(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [followsSystem]);

  const setTheme = useCallback((next: Theme) => {
    setFollowsSystem(false);
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setFollowsSystem(false);
    setThemeState((current) => (current === 'light' ? 'dark' : 'light'));
  }, []);
  return { theme, setTheme, toggle };
}
